import { Avatar as MuiAvatar, AvatarProps } from '@mui/material';
import { selectAvatarSize } from 'features/layout/features/dimensions/dimensionsSlice';
import { AvatarSize } from 'features/layout/features/dimensions/types';
import { useAppSelector } from 'store/hooks';

const scalingFactor = 0.3;
const maxSize = 120;
// Keeps the avatar off the tile's edge on a normal tile.
const edgeInset = 16;
// Floor for tiles too small for that inset: the avatar still takes this share of the tile.
const minTileShare = 0.6;
const maxGlyphShare = 0.5;

const sizes: Record<AvatarSize, number> = {
  xxs: 30,
  xs: 60,
  sm: 90,
  md: 120,
  lg: 150,
  xl: 190,
};

type CustomAvatarProps = AvatarProps & {
  size?: AvatarSize;
};

const Avatar = ({ children, sx = [], size, ...props }: CustomAvatarProps) => {
  const avatarSizeStr = useAppSelector(selectAvatarSize);
  const avatarSize = sizes[size || avatarSizeStr];
  const avatarFontSize = avatarSize * scalingFactor;

  const cappedSize = Math.min(avatarSize, maxSize);
  const insetBox = `(100cqmin - ${edgeInset * 2}px)`;

  return (
    <MuiAvatar
      {...props}
      sx={[
        {
          '--avatar-size': `${cappedSize}px`,
          '--avatar-font-size': `${avatarFontSize}px`,
          '@supports (width: 1cqmin)': {
            '--avatar-size': `min(${cappedSize}px, max(${insetBox}, ${minTileShare * 100}cqmin))`,
            '--avatar-font-size': `min(${avatarFontSize}px, ${insetBox} * ${scalingFactor})`,
          },
          width: 'var(--avatar-size)',
          height: 'var(--avatar-size)',
          flexShrink: 0,
          fontSize: `clamp(min(.75rem, var(--avatar-size) * ${maxGlyphShare}), var(--avatar-font-size), 4rem)`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </MuiAvatar>
  );
};
export default Avatar;
